import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { revenueCatService, SubscriptionStatus, PurchaseResult, UserAttributes } from '../services/revenueCatService';
import { useAuth } from './AuthContext';
import { RevenueCatEntitlement } from '../config/revenuecat.config';

interface RevenueCatContextType {
    isInitialized: boolean;
    subscriptionStatus: SubscriptionStatus | null;
    loading: boolean;
    error: string | null;

    // Methods
    purchaseProduct: (productId: string) => Promise<PurchaseResult>;
    restorePurchases: () => Promise<PurchaseResult>;
    hasEntitlement: (entitlement: RevenueCatEntitlement) => Promise<boolean>;
    refreshSubscriptionStatus: () => Promise<void>;
    updateUserEmail: (email: string) => Promise<void>;
    setUserAttributes: (attributes: UserAttributes) => Promise<void>;
    getUserAttributes: () => Promise<Record<string, string>>;
}

const RevenueCatContext = createContext<RevenueCatContextType | null>(null);

export const useRevenueCat = () => {
    const context = useContext(RevenueCatContext);
    if (!context) {
        throw new Error('useRevenueCat must be used within a RevenueCatProvider');
    }
    return context;
};

interface RevenueCatProviderProps {
    children: ReactNode;
}

export const RevenueCatProvider: React.FC<RevenueCatProviderProps> = ({ children }) => {
    const { currentUser, userData } = useAuth();
    const [isInitialized, setIsInitialized] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Initialize RevenueCat when user changes
    useEffect(() => {
        const initializeServices = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = currentUser?.uid;

                // Initialize RevenueCat
                await revenueCatService.initialize(userId);

                setIsInitialized(true);

                // Register user email and attributes if user is logged in
                if (userId && userData?.email) {
                    try {
                        await revenueCatService.registerUser(userId, userData.email, {
                            displayName: userData.fullName
                        });
                        console.log('✅ User email registered with RevenueCat during initialization');
                    } catch (emailError) {
                        console.error('⚠️ Failed to register user email with RevenueCat:', emailError);
                        // Don't throw error here as RevenueCat is still initialized
                    }
                }

                // Get initial subscription status with retry logic
                if (userId) {
                    console.log('🔄 Getting initial subscription status...');
                    
                    try {
                        // First try regular subscription status check
                        let status = await revenueCatService.getSubscriptionStatus();
                        console.log('🔍 Initial subscription check result:', status);
                        
                        // If no active subscription detected, force refresh and retry
                        if (!status.isActive) {
                            console.log('🔄 No active subscription detected, force refreshing...');
                            status = await revenueCatService.refreshSubscriptionStatus();
                            console.log('🔍 After force refresh result:', status);
                        }
                        
                        setSubscriptionStatus(status);
                    } catch (err) {
                        console.error('Failed to get subscription status during initialization:', err);
                        // Set a fallback status to avoid blocking the app
                        setSubscriptionStatus({
                            isActive: false,
                            entitlements: [],
                        });
                    }
                }
            } catch (err) {
                console.error('Failed to initialize RevenueCat:', err);
                setError(err instanceof Error ? err.message : 'Failed to initialize RevenueCat');
            } finally {
                setLoading(false);
            }
        };

        initializeServices();
    }, [currentUser, userData]);

    // Periodic subscription status check (every 5 minutes when app is active)
    useEffect(() => {
        if (!isInitialized || !currentUser) return;

        const checkSubscriptionPeriodically = async () => {
            try {
                console.log('🔄 Periodic subscription status check...');
                const status = await revenueCatService.getSubscriptionStatus();
                
                // Only update if the status actually changed to avoid unnecessary re-renders
                setSubscriptionStatus(current => {
                    if (!current) return status;
                    
                    const hasChanged = current.isActive !== status.isActive ||
                                     current.entitlements.length !== status.entitlements.length ||
                                     !current.entitlements.every(e => status.entitlements.includes(e));
                    
                    if (hasChanged) {
                        console.log('🔄 Subscription status changed:', { previous: current, new: status });
                        return status;
                    }
                    
                    return current;
                });
            } catch (err) {
                console.error('Periodic subscription check failed:', err);
            }
        };

        // Check immediately when becoming initialized
        checkSubscriptionPeriodically();
        
        // Then check every 5 minutes
        const interval = setInterval(checkSubscriptionPeriodically, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [isInitialized, currentUser]);

    // Handle user logout
    useEffect(() => {
        const handleLogout = async () => {
            if (!currentUser && isInitialized) {
                try {
                    await revenueCatService.logout();
                    setSubscriptionStatus(null);
                } catch (err) {
                    console.error('Failed to logout from RevenueCat:', err);
                }
            }
        };

        handleLogout();
    }, [currentUser, isInitialized]);

    const refreshSubscriptionStatus = async (): Promise<void> => {
        try {
            if (!isInitialized) return;

            console.log('🔄 Manual subscription status refresh...');
            const status = await revenueCatService.refreshSubscriptionStatus();
            console.log('🔍 Manual refresh result:', status);
            setSubscriptionStatus(status);
        } catch (err) {
            console.error('Failed to refresh subscription status:', err);
            setError(err instanceof Error ? err.message : 'Failed to refresh subscription status');
        }
    };

    const purchaseProduct = async (productId: string): Promise<PurchaseResult> => {
        try {
            if (!isInitialized) {
                return {
                    success: false,
                    error: 'RevenueCat is not initialized yet. Please wait.',
                };
            }

            setError(null);
            const result = await revenueCatService.purchaseProduct(productId);

            if (result.success) {
                // Refresh subscription status after successful purchase
                await refreshSubscriptionStatus();
            }

            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Purchase failed';
            setError(errorMessage);
            return {
                success: false,
                error: errorMessage,
            };
        }
    };

    const restorePurchases = async (): Promise<PurchaseResult> => {
        try {
            if (!isInitialized) {
                return {
                    success: false,
                    error: 'RevenueCat is not initialized yet. Please wait.',
                };
            }

            setError(null);
            const result = await revenueCatService.restorePurchases();

            if (result.success) {
                // Refresh subscription status after successful restore
                await refreshSubscriptionStatus();
            }

            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Restore failed';
            setError(errorMessage);
            return {
                success: false,
                error: errorMessage,
            };
        }
    };

    const hasEntitlement = async (entitlement: RevenueCatEntitlement): Promise<boolean> => {
        try {
            if (!isInitialized) {
                console.warn('RevenueCat is not initialized yet, returning false for entitlement check');
                return false;
            }

            return await revenueCatService.hasEntitlement(entitlement);
        } catch (err) {
            console.error('Failed to check entitlement:', err);
            return false;
        }
    };

    const updateUserEmail = async (email: string): Promise<void> => {
        try {
            if (!isInitialized) {
                throw new Error('RevenueCat is not initialized yet');
            }

            await revenueCatService.setUserEmail(email);
            console.log('✅ User email updated in RevenueCat');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update user email';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const setUserAttributes = async (attributes: UserAttributes): Promise<void> => {
        try {
            if (!isInitialized) {
                throw new Error('RevenueCat is not initialized yet');
            }

            await revenueCatService.setUserAttributes(attributes);
            console.log('✅ User attributes updated in RevenueCat');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to set user attributes';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const getUserAttributes = async (): Promise<Record<string, string>> => {
        try {
            if (!isInitialized) {
                throw new Error('RevenueCat is not initialized yet');
            }

            return await revenueCatService.getUserAttributes();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to get user attributes';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const value: RevenueCatContextType = {
        isInitialized,
        subscriptionStatus,
        loading,
        error,
        purchaseProduct,
        restorePurchases,
        hasEntitlement,
        refreshSubscriptionStatus,
        updateUserEmail,
        setUserAttributes,
        getUserAttributes,
    };

    return (
        <RevenueCatContext.Provider value={value}>
            {children}
        </RevenueCatContext.Provider>
    );
};